import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Table,
  Alert,
  Container,
  Card,
  Button,
  Badge,
  Row,
  Col,
} from "react-bootstrap";
import { Spinner } from "reactstrap"
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "../components/HusariaHeader";
import { useNavigate } from "react-router-dom";
import { Bar } from "react-chartjs-2";

const CallsOverview = () => {
  localStorage.removeItem("selectedSite")
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [calls, setCalls] = useState([]);
  const [error, setError] = useState(null);
  const [directionFilter, setDirectionFilter] = useState("all");
  const navigate = useNavigate();

  // Chart state
  const [dailyData, setDailyData] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [chartsError, setChartsError] = useState(null);
  const [chartLoading, setChartsLoading] = useState(false);
  const fetchActiveCalls = async () => {
    try {
      const response = await fetch(`${serverHost}/active_calls/all`);
      if (!response.ok) throw new Error("Failed to fetch active calls");
      const data = await response.json();
      setCalls(data.result);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchCallHistory = async () => {
    setChartsLoading(true);
    try {
      const response = await fetch(`${serverHost}/call_history/all`);
      if (!response.ok) throw new Error("Failed to fetch active calls");
      const data = await response.json();
      setDailyData(data.daily);
      setHourlyData(data.hourly);
    } catch (err) {
      setChartsError(err.message);
    } finally {
      setChartsLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveCalls();
    fetchCallHistory();
    const interval = setInterval(fetchActiveCalls, 5000);
    return () => clearInterval(interval);
  }, []);

  // --- Chart configs ---
  const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";
  const textColor = isDark ? "#cbd5e1" : "#374151";
  const tooltipBg = isDark ? "#1e293b" : "#fff";

  const dailyChartData = {
    labels: dailyData.map((d) => {
      const dt = new Date(d.day);
      return dt.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    }),
    datasets: [
      {
        label: "Calls",
        data: dailyData.map((d) => d.call_count),
        backgroundColor: dailyData.map((d) =>
          d.day === selectedDate
            ? isDark
              ? "rgba(99,179,237,0.95)"
              : "rgba(37,99,235,0.85)"
            : isDark
              ? "rgba(99,179,237,0.35)"
              : "rgba(37,99,235,0.3)",
        ),
        borderColor: dailyData.map((d) =>
          d.day === selectedDate
            ? isDark
              ? "#63b3ed"
              : "#1d4ed8"
            : isDark
              ? "rgba(99,179,237,0.6)"
              : "rgba(37,99,235,0.5)",
        ),
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const dailyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: isDark ? "#334155" : "#e2e8f0",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          title: (items) => dailyData[items[0].dataIndex]?.date ?? "",
          label: (item) => ` ${item.raw} calls`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 11 } },
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor },
        ticks: {
          color: textColor,
          font: { size: 11 },
          stepSize: 1,
          precision: 0,
        },
      },
    },
  };

  // Build full 24-hour labels
  const hourlyChartData = {
    labels: hourlyData.map((h) => {
      const dt = new Date(h.hour_bucket);
      return dt.toLocaleString("en-US", {
        weekday: "short",
        hour: "numeric",
        hour12: true,
      });
    }),
    datasets: [
      {
        label: "Calls",
        data: hourlyData.map((h) => h.call_count),
        backgroundColor: hourlyData.map((h) =>
          h.day === selectedDate
            ? isDark
              ? "rgba(99,179,237,0.95)"
              : "rgba(37,99,235,0.85)"
            : isDark
              ? "rgba(99,179,237,0.35)"
              : "rgba(37,99,235,0.3)",
        ),
        borderColor: hourlyData.map((h) =>
          h.day === selectedDate
            ? isDark
              ? "#63b3ed"
              : "#1d4ed8"
            : isDark
              ? "rgba(99,179,237,0.6)"
              : "rgba(37,99,235,0.5)",
        ),
        borderWidth: 2,
        borderRadius: 6,
        borderSkipped: false,
      },
    ],
  };

  const hourlyChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: tooltipBg,
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: isDark ? "#334155" : "#e2e8f0",
        borderWidth: 1,
        padding: 10,
        callbacks: {
          label: (item) => ` ${item.raw} calls`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 10 } },
      },
      y: {
        beginAtZero: true,
        suggestedMax: 5,
        grid: { color: gridColor },
        ticks: {
          color: textColor,
          font: { size: 11 },
          stepSize: 1,
          precision: 0,
        },
      },
    },
  };

  // --- Existing logic ---
  const filteredCalls = calls.filter((call) => {
    if (directionFilter === "all") return true;
    return call.direction?.toLowerCase() === directionFilter;
  });

  const activeCallsCount = calls.length;
  const inboundCount = calls.filter(
    (c) => c.direction?.toLowerCase() === "inbound",
  ).length;
  const outboundCount = calls.filter(
    (c) => c.direction?.toLowerCase() === "outbound",
  ).length;
  const localCount = calls.filter(
    (c) => c.direction?.toLowerCase() === "local",
  ).length;

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const hangup = async (call, suppressRefresh = false) => {
    try {
      const resp = await fetch(`${serverHost}/hangup/${call}`);
      if (!resp.ok) throw new Error("Failed to hang up call " + call);
      if (!suppressRefresh) fetchActiveCalls();
    } catch (err) {
      console.log("Failed to hang up ", err);
      setError("Failed to hangup call: " + err);
    }
  };

  const hangupAll = async () => {
    await Promise.all(calls.map((call) => hangup(call.uuid, true)));
    fetchActiveCalls();
  };

  const getDirectionBadge = (direction) => {
    const dirLower = direction?.toLowerCase();
    if (dirLower === "inbound") return <Badge bg="success">Inbound</Badge>;
    if (dirLower === "outbound") return <Badge bg="primary">Outbound</Badge>;
    if (dirLower === "local") return <Badge bg="info">Local</Badge>;
    if (dirLower === "emergency") return <Badge bg="warning">Emergency</Badge>;
    return <Badge bg="secondary">{direction || "Unknown"}</Badge>;
  };

  const handleDomainClick = async (domain) => {
    try {
      const response = await fetch(`${serverHost}/fetch_site_info/${domain}`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) throw new Error("Failed to site info.");
      const data = await response.json();
      localStorage.setItem("selectedSite", JSON.stringify(data.site));
      localStorage.setItem("clientSites", JSON.stringify(data.client_sites));
      localStorage.setItem("clientId", JSON.stringify(data.client_uuid));
      window.dispatchEvent(
        new CustomEvent("siteChanged", { detail: data.site }),
      );
      navigate("/admin/hucs/active-calls");
    } catch (err) {
      console.log("error: failed to get domain ", err);
    }
  };

  const info_style = {
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderRadius: "1rem",
    overflow: "hidden",
  };

  const cardHeaderStyle = {
    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
    color: isDark ? "#e2e8f0" : "#111",
  };

  const formattedSelectedDate = selectedDate
    ? new Date(selectedDate).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <>
      <HusariaHeader
        name="Calls Dashboard"
        parentName="Dealer"
        padding="9.5rem"
      />

      <Container fluid className="mt--9">
        <div style={{ marginBottom: "8px" }}>
          <span
            className="return-dealer-menu"
            onClick={() => navigate("/admin/dealer-menu")}
          >
            <i className="fas fa-caret-left"></i> Return to Dealer Menu
          </span>
        </div>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Active Calls
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700 }}>
                      {activeCallsCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                    <i className="fas fa-phone fa-rotate-90"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Inbound
                    </p>
                    <h2
                      className="mb-0"
                      style={{ fontWeight: 700, color: "#28a745" }}
                    >
                      {inboundCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                    <i className="fas fa-sign-in-alt"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Outbound
                    </p>
                    <h2
                      className="mb-0"
                      style={{ fontWeight: 700, color: "#007bff" }}
                    >
                      {outboundCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                    <i className="fas fa-sign-out-alt"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Local
                    </p>
                    <h2
                      className="mb-0"
                      style={{ fontWeight: 700, color: "#17a2b8" }}
                    >
                      {localCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.9 }}>
                    <i className="fas fa-map-pin"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Charts Row */}
        {chartsError ? (
          <Alert variant="warning" className="mb-4">
            Could not load call history charts: {chartsError}
          </Alert>
        ) : (
          <Row className="mb-4">
            {/* Daily Chart */}
            <Col lg="6" className="mb-3">
              <Card
                className={`shadow-sm h-100 ${isDark ? "bg-dark text-light border-dark" : ""}`}
                style={info_style}
              >
                <Card.Header style={cardHeaderStyle}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">CALLS — LAST 7 DAYS</h4>
                  </div>
                </Card.Header>
                <Card.Body
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#fff",
                    height: "260px",
                  }}
                >
                  {chartLoading ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      <Spinner animation="border" variant={isDark ? "light" : "primary"} />
                      <span style={{marginLeft: "10px"}}>Loading last 7 days, Please Wait</span>
                    </div>
                  ) : dailyData.length === 0 ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      No data available
                    </div>
                  ) : (
                    <Bar data={dailyChartData} options={dailyChartOptions} />
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Hourly Chart */}
            <Col xs="12" md="6" className="mb-3">
              <Card
                className={`shadow-sm h-100 ${isDark ? "bg-dark text-light border-dark" : ""}`}
                style={info_style}
              >
                <Card.Header style={cardHeaderStyle}>
                  <div className="d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">CALLS BY HOUR — LAST 24 HOURS</h4>
                    {selectedDate && (
                      <small style={{ color: isDark ? "#94a3b8" : "#6c757d" }}>
                        {formattedSelectedDate}
                      </small>
                    )}
                  </div>
                </Card.Header>
                <Card.Body
                  style={{
                    backgroundColor: isDark ? "#1f2937" : "#fff",
                    height: "260px",
                  }}
                >
                  {chartLoading ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      <Spinner animation="border" variant={isDark ? "light" : "primary"} />
                      <span style={{marginLeft: "10px"}}>Loading last 24 hours, Please Wait</span>
                    </div>
                  ) :!hourlyData ? (
                    <div
                      className="d-flex align-items-center justify-content-center h-100"
                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                    >
                      No Data Available
                    </div>
                  ) : (
                    <Bar data={hourlyChartData} options={hourlyChartOptions} />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Active Calls Table */}
        <Card
          className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
        >
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={cardHeaderStyle}
          >
            <h4 className="mb-0">ACTIVE CALLS</h4>
            <div className="d-flex gap-2">
              <Button
                variant={
                  directionFilter === "all" ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setDirectionFilter("all")}
              >
                All ({activeCallsCount})
              </Button>
              <Button
                variant={
                  directionFilter === "inbound" ? "success" : "outline-success"
                }
                size="sm"
                onClick={() => setDirectionFilter("inbound")}
              >
                Inbound ({inboundCount})
              </Button>
              <Button
                variant={
                  directionFilter === "outbound" ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setDirectionFilter("outbound")}
              >
                Outbound ({outboundCount})
              </Button>
              <Button
                variant={directionFilter === "local" ? "info" : "outline-info"}
                size="sm"
                onClick={() => setDirectionFilter("local")}
              >
                Local ({localCount})
              </Button>
              <Button variant="info" size="sm" onClick={fetchActiveCalls}>
                Refresh
              </Button>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            {error ? (
              <Alert variant="danger" className="m-3">
                {error}
              </Alert>
            ) : filteredCalls.length === 0 ? (
              <div
                className="text-center py-5"
                style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
              >
                <h5>No active calls</h5>
                <p className="mb-0">
                  There are currently no{" "}
                  {directionFilter !== "all" ? directionFilter : ""} calls in
                  progress.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table
                  striped
                  hover
                  responsive
                  bordered
                  className="mb-0"
                  style={{
                    minWidth: "1000px",
                    backgroundColor: isDark ? "#1f2937" : "#fff",
                    color: isDark ? "#e2e8f0" : "#212529",
                  }}
                >
                  <thead
                    style={{
                      backgroundColor: isDark ? "#1f2937" : "#fff",
                      fontWeight: "bold",
                      color: isDark ? "#e2e8f0" : "#212529",
                    }}
                  >
                    <tr>
                      <th className="text-center">Profile</th>
                      <th className="text-center">Call ID</th>
                      <th className="text-center">Direction</th>
                      <th className="text-center">Domain</th>
                      <th className="text-center">Caller</th>
                      <th className="text-center">Destination</th>
                      <th className="text-center">Duration</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCalls.map((call) => (
                      <tr
                        key={call.uuid}
                        style={{ color: isDark ? "#e2e8f0" : "#212529" }}
                      >
                        <td
                          className="text-center"
                          style={{ fontFamily: "monospace" }}
                        >
                          {call.profile || "-"}
                        </td>
                        <td
                          className="text-center"
                          style={{ fontFamily: "monospace" }}
                        >
                          {call.call_id || "-"}
                        </td>
                        <td className="text-center">
                          {getDirectionBadge(call.direction)}
                        </td>
                        <td
                          className="text-center"
                          style={{
                            cursor: "pointer",
                            color: isDark
                              ? "rgb(96, 165, 250)"
                              : "rgb(13, 110, 253)",
                          }}
                          onClick={() => handleDomainClick(call.domain)}
                        >
                          {call.domain || "-"}
                        </td>
                        <td className="text-center">
                          {call.caller_number || "-"}
                        </td>
                        <td className="text-center">
                          {call.callee_number || "-"}
                        </td>
                        <td className="text-center">
                          {formatDuration(call.duration)}
                        </td>
                        <td className="text-center">
                          <Badge bg="warning" text="dark">
                            {call.status || "Active"}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => hangup(call.uuid)}
                          >
                            Hang Up
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default CallsOverview;
